import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { TenantService } from './tenant.service';
import { MessagesService } from '../shared/services/messages.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CourseDeleteService {
  constructor(
    private http: HttpClient,
    private tenantService: TenantService,
    private messages: MessagesService
  ) {}

  /**
   * Delete a regular course with cascade cleanup for read-only clones
   * This calls the backend endpoint that handles:
   * 1. Finding and deleting all read-only cloned courses and their lessons/sections
   * 2. Deleting lessons and sections for the original course
   * 3. Deleting the original course
   */
  deleteCourseWithCleanup(courseId: string): Observable<any> {
    const tenantId = this.tenantService.tenant.id;
    
    const requestBody = {
      courseId,
      tenantId
    };

    console.log('Calling course cascade deletion endpoint...', requestBody);
    
    return this.http.post(environment.api.deleteCourseUrl, requestBody).pipe(
      map((response: any) => {
        console.log('Course deletion response:', response);
        if (response.success) {
          console.log(`Course cascade deletion completed:`, response.details);
          let successMessage = `Course deleted successfully.`;
          
          // Only include lessons and sections message if any were deleted
          if (response.details.lessonsDeleted > 0 || response.details.sectionsDeleted > 0) {
            successMessage += ` ${response.details.lessonsDeleted} lessons and ${response.details.sectionsDeleted} sections were removed.`;
          }
          
          // Add cloned courses message
          if (response.details.clonedCoursesDeleted > 0) {
            successMessage += ` ${response.details.clonedCoursesDeleted} read-only cloned courses were also deleted.`;
          }
          
          this.messages.success(successMessage);
        }
        return response;
      }),
      catchError(error => {
        console.error('Course deletion failed:', error);
        this.messages.error('Failed to delete course with cleanup: ' + (error.error?.error || error.message));
        return throwError(() => error);
      })
    );
  }

  /**
   * Delete a SCORM course with full cascade cleanup (clones + storage)
   * This calls the backend endpoint that handles:
   * 1. Finding and deleting all cloned courses
   * 2. Cleaning up SCORM storage files
   * 3. Deleting the original course
   */
  deleteScormCourseWithCleanup(courseId: string): Observable<any> {
    const tenantId = this.tenantService.tenant.id;
    
    const requestBody = {
      courseId,
      tenantId
    };

    console.log('Calling SCORM cascade deletion endpoint...', requestBody);
    
    return this.http.post(environment.api.deleteScormCourseUrl, requestBody).pipe(
      map((response: any) => {
        console.log('SCORM deletion response:', response);
        if (response.success) {
          console.log(`SCORM cascade deletion completed:`, response.details);
          let successMessage = `SCORM course deleted successfully.`;
          
          if (response.details.clonedCoursesDeleted > 0) {
            successMessage += ` ${response.details.clonedCoursesDeleted} cloned courses`;
            
            if (response.details.storageFilesDeleted > 0) {
              successMessage += ` and ${response.details.storageFilesDeleted} storage files were cleaned up.`;
            } else {
              successMessage += ` were deleted.`;
            }
          } 
          else if (response.details.storageFilesDeleted > 0) {
            successMessage += ` ${response.details.storageFilesDeleted} storage files were cleaned up.`;
          }
          
          this.messages.success(successMessage);
        }
        return response;
      }),
      catchError(error => {
        console.error('SCORM deletion failed:', error);
        this.messages.error('Failed to delete SCORM course with cleanup: ' + (error.error?.error || error.message));
        return throwError(() => error);
      })
    );
  }
}
